
import React, { useState, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMailboxes } from '@/hooks/useMailboxes'
import { useWorkspaceClient } from '@/hooks/useWorkspaceClient'
import { Button } from "@/components/ui/button"
import { RefreshCw, X } from 'lucide-react'
import { MailboxMetrics } from './MailboxMetrics'
import { MailboxAnalyticsSection } from './MailboxAnalyticsSection'
import { CompactMailboxTable } from './CompactMailboxTable'
import { BulkActionToolbar } from './BulkActionToolbar'
import { BulkOperationsList } from './BulkOperationsList'
import { AdvancedFilter, FilterRule } from '@/components/ui/advanced-filter'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { isPrimaryDomain } from '@/lib/primary-domains'


interface MailboxDashboardProps {
  performSync?: (platform: string) => void
  isSyncing?: boolean
  progress?: any
  stats?: any
  isBackgroundSyncRunning?: boolean
  schedulerStatus?: any
}

export function MailboxDashboard({ 
  performSync, 
  isSyncing, 
  progress, 
  stats, 
  isBackgroundSyncRunning, 
  schedulerStatus 
}: MailboxDashboardProps) {
  const [searchParams] = useSearchParams()
  const workspace = useWorkspaceClient()
  const { data: mailboxes = [], isLoading, refetch } = useMailboxes(workspace.mailboxClientId)
  const [selectedMailboxes, setSelectedMailboxes] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<FilterRule[]>([])
  const [lastGlobalSync, setLastGlobalSync] = useState<string>('')
  const [clientFilter, setClientFilter] = useState<string | null>(null)
  const [warmupStatusFilter, setWarmupStatusFilter] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string | null>(null)
  const [domainFilter, setDomainFilter] = useState<string[]>([])
  const { toast } = useToast()

  // Handle domain filtering from URL params
  React.useEffect(() => {
    const domains = searchParams.get('domains')
    if (domains) {
      const domainList = domains.split(',')
      setDomainFilter(domainList)
      
      // Create a single advanced filter rule with comma-separated domains
      const domainFilterRule: FilterRule = {
        id: 'domain-filter',
        field: 'domain',
        operator: 'in',
        value: domainList.join(', ')
      }
      
      setFilters([domainFilterRule])
    } else {
      setDomainFilter([])
      setFilters([])
    }
  }, [searchParams])

  // Get last global sync time
  React.useEffect(() => {
    const getLastSync = async () => {
      const { data } = await supabase
        .from('mailboxes')
        .select('last_api_synced_at')
        .order('last_api_synced_at', { ascending: false })
        .limit(1)
        .single()
      
      if (data?.last_api_synced_at) {
        setLastGlobalSync(data.last_api_synced_at)
      }
    }
    getLastSync()
  }, [])

  const applyFilters = (data: any[], query: string, filterRules: FilterRule[], clientId: string | null, warmupStatus: string | null, status: string | null, domains: string[] = []) => {
    let filtered = data

    // Apply domain filter
    if (domains.length > 0) {
      filtered = filtered.filter(item => domains.some(domain => item.domain?.includes(domain)))
    }

    // Apply client filter
    if (clientId !== null) {
      if (clientId === 'other') {
        filtered = filtered.filter(item => !item.client_id || (item.client_id !== '5559' && item.client_id !== '8241'))
      } else {
        filtered = filtered.filter(item => item.client_id === clientId)
      }
    }

    // Apply warmup status filter - only apply to successfully synced mailboxes
    if (warmupStatus !== null) {
      // First filter by successful sync status to match the metrics calculation
      const successfulSyncMailboxes = filtered.filter(item => item.api_sync_status === 'SUCCESS')
      
      if (warmupStatus === 'ACTIVE') {
        filtered = successfulSyncMailboxes.filter(item => item.warmup_status === 'ACTIVE')
      } else if (warmupStatus === 'INACTIVE') {
        // For INACTIVE, exclude primary domains and deactivated accounts to match the count calculation
        // Now includes both INACTIVE and NULL values
        filtered = successfulSyncMailboxes.filter(item => {
          if (isPrimaryDomain(item.email)) {
            return false
          }
          
          if (item.status === 'DEACTIVATED') {
            return false
          }
          
          return item.warmup_status === 'INACTIVE' || item.warmup_status === null || item.warmup_status === undefined
        })
      }
    }


    // Apply status filter
    if (status !== null) {
      filtered = filtered.filter(item => {
        // Handle deactivated status specially - check api_sync_status
        if (status === 'DEACTIVATED') {
          return item.api_sync_status === 'DEACTIVATED'
        }
        // For other statuses, check the status field
        return item.status === status
      })
    }

    // Apply search query
    if (query) {
      filtered = filtered.filter(item => 
        item.email?.toLowerCase().includes(query.toLowerCase()) ||
        item.from_name?.toLowerCase().includes(query.toLowerCase()) ||
        item.client_name?.toLowerCase().includes(query.toLowerCase()) ||
        item.domain?.toLowerCase().includes(query.toLowerCase())
      )
    }

    // Apply advanced filters
    if (filterRules.length > 0) {
      console.log('Applying advanced filters:', filterRules)
      console.log('Pre-filter data count:', filtered.length)
      filtered = filtered.filter(item => {
        const result = evaluateFilterGroup(item, filterRules)
        if (!result) {
          console.log('Item filtered out:', item.email, 'status:', item.status)
        }
        return result
      })
      console.log('Post-filter data count:', filtered.length)
    }

    return filtered
  }

  const evaluateFilterGroup = (item: any, rules: FilterRule[]) => {
    if (rules.length === 0) return true
    
    let result = evaluateRule(item, rules[0])
    
    for (let i = 1; i < rules.length; i++) {
      const rule = rules[i]
      const ruleResult = evaluateRule(item, rule)
      
      if (rule.connector === 'OR') {
        result = result || ruleResult
      } else {
        result = result && ruleResult
      }
    }
    
    return result
  }

  const evaluateRule = (item: any, rule: FilterRule) => {
    let value = item[rule.field]
    let filterValue = rule.value

    // Debug logging
    console.log(`Evaluating rule:`, { 
      field: rule.field, 
      operator: rule.operator, 
      filterValue, 
      itemValue: value,
      itemEmail: item.email
    })

    // Special handling for email_type filter
    if (rule.field === 'email_type') {
      const email = item.email || ''
      const parts = email.split('@')[0]
      const isPrimary = !parts.includes('+') && !parts.includes('.')
      value = isPrimary ? 'primary' : 'secondary'
    }

    // Handle boolean fields — normalize to string for comparison
    if (typeof value === 'boolean') {
      value = String(value)
      filterValue = String(filterValue).toLowerCase()
    } else if ((filterValue === 'true' || filterValue === 'false') && (value === null || value === undefined)) {
      // Null/undefined treated as false for boolean-like fields
      value = 'false'
      filterValue = String(filterValue).toLowerCase()
    }

    // Handle case-insensitive comparison for text fields (except for numbers/booleans)
    if (typeof value === 'string' && typeof filterValue === 'string' &&
        !['greater_than', 'less_than', 'greater_equal', 'less_equal'].includes(rule.operator)) {
      // For text comparisons, normalize case for consistent matching
      value = value.toUpperCase()
      filterValue = filterValue.toUpperCase()
    }

    let result = false
    
    // Debug: log the comparison values after normalization
    console.log(`After normalization - Field: ${rule.field}, Operator: ${rule.operator}, Value: "${value}", FilterValue: "${filterValue}"`)
    
    switch (rule.operator) {
      case 'contains':
        result = String(value || '').includes(String(filterValue))
        break
      case 'equals':
        result = String(value) === String(filterValue)
        break
      case 'starts_with':
        result = String(value || '').startsWith(String(filterValue))
        break
      case 'ends_with':
        result = String(value || '').endsWith(String(filterValue))
        break
      case 'not_contains':
        result = !String(value || '').includes(String(filterValue))
        break
      case 'not_equals':
        result = String(value) !== String(filterValue)
        break
      case 'is_empty':
        result = !value || value === ''
        break
      case 'is_not_empty':
        result = value && value !== ''
        break
      case 'greater_than':
        result = Number(value) > Number(filterValue)
        break
      case 'less_than':
        result = Number(value) < Number(filterValue)
        break
      case 'greater_equal':
        result = Number(value) >= Number(filterValue)
        break
      case 'less_equal':
        result = Number(value) <= Number(filterValue)
        break
      case 'in':
        // Handle comma-separated values for 'in' operator
        if (filterValue) {
          const values = filterValue.split(',').map(v => v.trim().toUpperCase())
          result = values.some(v => String(value || '').toUpperCase().includes(v))
        }
        break
      default:
        result = true
    }
    
    // Debug: log the result of the evaluation
    console.log(`Rule result: ${result} for ${item.email} (${rule.field}: ${item[rule.field]})`)
    
    return result
  }

  const filteredMailboxes = useMemo(() => {
    return applyFilters(mailboxes, searchQuery, filters, clientFilter, warmupStatusFilter, statusFilter, domainFilter)
  }, [mailboxes, searchQuery, filters, clientFilter, warmupStatusFilter, statusFilter, domainFilter])

  const formatLastSync = (lastSync: string) => {
    if (!lastSync) return 'Never'
    try {
      const date = new Date(lastSync)
      if (isNaN(date.getTime()) || date > new Date()) return 'Never'
      return date.toLocaleString()
    } catch {
      return 'Never'
    }
  }

  const handleRefresh = () => {
    refetch()
    toast({
      title: "Refreshed",
      description: "Mailbox data has been refreshed",
    })
  }

  const handleClientFilter = (clientId: string | null) => {
    setClientFilter(clientId === clientFilter ? null : clientId)
  }

  const handleWarmupStatusFilter = (status: string) => {
    setWarmupStatusFilter(status === warmupStatusFilter ? null : status)
    
    // Scroll to table after a short delay to allow state update
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }


  const handleStatusFilter = (status: string) => {
    setStatusFilter(status === statusFilter ? null : status)
    
    // Scroll to table after a short delay to allow state update
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  // New click handlers for metric cards
  const handleTotalMailboxesClick = () => {
    // Clear all filters to show all mailboxes
    setWarmupStatusFilter(null)
    setStatusFilter(null)
    setClientFilter(null)
    setDomainFilter([])
    setFilters([])
    
    // Scroll to table
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleActiveMailboxesClick = () => {
    // Filter to show only active mailboxes (not deactivated)
    setWarmupStatusFilter(null)
    setStatusFilter('ACTIVE')
    setClientFilter(null)
    setDomainFilter([])
    setFilters([])
    
    // Scroll to table
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleDeactivatedMailboxesClick = () => {
    // Filter to show only deactivated mailboxes
    setWarmupStatusFilter(null)
    setStatusFilter('DEACTIVATED')
    setClientFilter(null)
    setDomainFilter([])
    setFilters([])
    
    // Scroll to table
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleDailyCapacityClick = () => {
    // Filter to show only successfully synced mailboxes (those contributing to daily capacity)
    setWarmupStatusFilter(null)
    setStatusFilter(null)
    setClientFilter(null)
    setDomainFilter([])
    setFilters([{
      id: 'api-sync-status',
      field: 'api_sync_status',
      operator: 'equals',
      value: 'SUCCESS'
    }])
    
    // Scroll to table
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handleSmartleadSyncedClick = () => {
    // Filter to show only successfully synced mailboxes
    setWarmupStatusFilter(null)
    setStatusFilter(null)
    setClientFilter(null)
    setDomainFilter([])
    setFilters([{
      id: 'api-sync-status',
      field: 'api_sync_status',
      operator: 'equals',
      value: 'SUCCESS'
    }])
    
    // Scroll to table
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  const handlePrimaryDomainsClick = () => {
    // Filter to show only primary domain mailboxes
    setWarmupStatusFilter(null)
    setStatusFilter(null)
    setClientFilter(null)
    setDomainFilter([])
    setFilters([{
      id: 'primary-domains',
      field: 'email',
      operator: 'in',
      value: 'yotpo.com,loopreturns.com,tryyotpotoday.com,yotpouk.com,yotpoglobal.com,joinyotpotoday.com'
    }])
    
    // Scroll to table
    setTimeout(() => {
      const tableElement = document.getElementById('mailbox-table')
      if (tableElement) {
        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }



  return (
    <div className="space-y-6">

      {/* Email Infrastructure - matches Domain Infrastructure position in Domains tab */}
      <MailboxMetrics
        section="infrastructure"
        mailboxes={mailboxes}
        onWarmupStatusClick={handleWarmupStatusFilter}
        activeWarmupFilter={warmupStatusFilter}
        onStatusClick={handleStatusFilter}
        activeStatusFilter={statusFilter}
        onTotalMailboxesClick={handleTotalMailboxesClick}
        onActiveMailboxesClick={handleActiveMailboxesClick}
        onDeactivatedMailboxesClick={handleDeactivatedMailboxesClick}
        onDailyCapacityClick={handleDailyCapacityClick}
        onSmartleadSyncedClick={handleSmartleadSyncedClick}
        onPrimaryDomainsClick={handlePrimaryDomainsClick}
      />

      {/* Active filters indicator */}
      {(warmupStatusFilter || statusFilter || domainFilter.length > 0) && (
        <div className="space-y-2">
          {domainFilter.length > 0 && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="text-sm text-green-700 font-medium">
                Domain Filter: {domainFilter.join(', ')}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setDomainFilter([])}
                className="h-6 w-6 p-0 text-green-600 hover:text-green-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          {warmupStatusFilter && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm text-blue-700 font-medium">
                Active Filter: Warmup {warmupStatusFilter === 'NULL' ? 'Null' : warmupStatusFilter}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setWarmupStatusFilter(null)}
                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          {statusFilter && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <span className="text-sm text-blue-700 font-medium">
                Active Filter: Status {statusFilter}
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setStatusFilter(null)}
                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-800"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Analytics Section */}
      <MailboxAnalyticsSection
        mailboxes={mailboxes}
        onFilterChange={(filterType, filterValue) => {
          // Handle analytics filter changes
          if (filterType === 'provider') {
            toast({
              title: "Filter applied",
              description: `Filtering by ${filterType}: ${filterValue}`,
            })
          } else if (filterType === 'warmup_status') {
            handleWarmupStatusFilter(filterValue)
          }
        }}
      />

      {/* Bulk Actions - positioned right above the table */}
      {selectedMailboxes.length > 0 && (
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          <div className="p-4">
            <BulkActionToolbar
              selectedCount={selectedMailboxes.length}
              selectedMailboxes={selectedMailboxes}
              totalMailboxes={filteredMailboxes.length}
              onUpdate={refetch}
              onClearSelection={() => setSelectedMailboxes([])}
              onSelectAll={() => setSelectedMailboxes(filteredMailboxes.map(m => m.id))}
            />
          </div>
        </div>
      )}

      {/* Mailboxes Table with integrated search and filter */}
      <div id="mailbox-table">
        <CompactMailboxTable
          mailboxes={filteredMailboxes}
          isLoading={isLoading}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filters={filters}
          onFiltersChange={setFilters}
          selectedMailboxes={selectedMailboxes}
          onSelectionChange={setSelectedMailboxes}
          onMailboxUpdate={refetch}
        />
      </div>

      {/* Smartlead Two-Way Sync - below table */}
      <MailboxMetrics
        section="sync"
        mailboxes={mailboxes}
        onWarmupStatusClick={handleWarmupStatusFilter}
        activeWarmupFilter={warmupStatusFilter}
        performSync={performSync}
        isSyncing={isSyncing}
        progress={progress}
        stats={stats}
        isBackgroundSyncRunning={isBackgroundSyncRunning}
        schedulerStatus={schedulerStatus}
      />

      {/* Bulk Operations History */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="border-l-4 border-orange-500 pl-6 py-2 mb-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Recent Bulk Operations</h3>
            <p className="text-gray-600">View history of deactivations and other bulk operations</p>
          </div>
          <BulkOperationsList />
        </div>
      </div>
    </div>
  )
}
